import DatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { authState } from '../../../atoms/authState';
import { fetchCompanyMypage, fetchDriverMypage } from '../../../api/mypage';
import { formatLocalDate } from '../../../utils/util';
import { zodResolver } from '@hookform/resolvers/zod';
import { inquiryRegistrationSchema } from '../../../schema/inquirySchema';
import { useHistory } from 'react-router-dom';
import { AlertModal } from '../../../components/modal/alert.modal';
import { inquiryRagistration } from '../../../api/inquiry';

interface DriverState {
  vehicle: string;
  phone: string;
  registration: string;
  to_user_id: string | null;
}

interface CompanyState {
  passid: string;
  name: string;
  charge: string;
  position: string;
  phone: string;
  address: string;
  registration: string;
}

interface InquiryDto {
  title: string;
  question: string;
}

const InquiryRegist = () => {
  const auth = useRecoilValue(authState);

  const today = new Date();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InquiryDto>({
    resolver: zodResolver(inquiryRegistrationSchema),
  });

  const [profileData, setProfileData] = useState<DriverState | null>(null);
  const [companyProfileData, setCompanyProfileData] =
    useState<CompanyState | null>(null);

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/inquiry',
      });
    }
  };

  const onSubmit = async (data: InquiryDto) => {
    try {
      const response = await inquiryRagistration(data); // 데이터 전송
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('문의 등록에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error('Failed to update order:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (auth.userType === 'driver') {
          // 드라이버 데이터 가져오기
          const driverData = await fetchDriverMypage();
          setProfileData(driverData);
        } else if (auth.userType === 'company') {
          // 회사 데이터 가져오기 (추가적인 API 호출이 필요합니다)
          const companyData = await fetchCompanyMypage(); // fetchCompanyMypage()는 회사 데이터를 가져오는 API 호출 함수라고 가정합니다.
          setCompanyProfileData(companyData);
        }
      } catch (error) {
        console.error('데이터를 불러오는 데 실패했습니다.');
      }
    };

    fetchData();
  }, [auth.userType]);

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="car-info">
          <ul>
            <li>
              <div className="label">
                {auth.userType === 'driver' ? '차량 번호' : '아이디'}
              </div>
              <div className="value">
                {auth.userType === 'driver'
                  ? profileData?.vehicle
                  : companyProfileData?.passid}
              </div>
            </li>
            <li>
              <div className="label">휴대폰 번호</div>
              <div className="value">
                {auth.userType === 'driver'
                  ? profileData?.phone
                  : companyProfileData?.phone}
              </div>
            </li>
            <li>
              <div className="label">등록일</div>
              <div className="value">{formatLocalDate(today)}</div>
            </li>
          </ul>
        </div>
        <div className="contents">
          <div className="title">
            제목 <span>*</span>
          </div>
          <div className="input-box">
            <input type="text" {...register('title')} />
            {errors.title && <ValTxt>{String(errors.title.message)}</ValTxt>}
          </div>
          <div className="title">
            내용 <span>*</span>
          </div>
          <div className="text-box-wrap">
            <div className="text-box">
              <textarea
                {...register('question')}
                placeholder="1:1 문의에 등록할 내용을 입력해주세요."
              ></textarea>
            </div>
            {errors.question && (
              <ValTxt>{String(errors.question.message)}</ValTxt>
            )}
          </div>
        </div>
        <Btn>등록 하기</Btn>
      </form>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default InquiryRegist;

// style
const Container = styled.div`
  height: 100%;
  padding: 0 1.5rem 11.4rem;
  form {
    padding: 2.4rem 0;
    height: 100%;
    .car-info {
      padding: 2.4rem 2.4rem 2.8rem;
      border-radius: 1.6rem;
      box-shadow: 0 2px 5px 3px rgba(0, 0, 0, 0.05);
      position: relative;
      ul {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        li {
          display: flex;
          gap: 2.4rem;
          align-items: center;
          .label {
            min-width: 5.6rem;
            font-size: 1.2rem;
            font-weight: 600;
            color: ${(props) => props.theme.textAAAColor};
            line-height: 1.6rem;
          }
          .value {
            font-size: 1.4rem;
            font-weight: 600;
            &.status {
              min-width: 4.2rem;
              color: ${(props) => props.theme.textWhiteColor};
              background-color: ${(props) => props.theme.btnBlueColor};
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0.4rem;
              font-size: 1.1rem;
              border-radius: 1.1rem;
            }
          }
        }
      }
    }
    .contents {
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      margin-top: 3.2rem;
      height: calc(100% - 16rem);
      .title {
        font-size: 1.5rem;
        font-weight: 600;
        display: flex;
        align-items: flex-start;
        span {
          color: #ff5151;
          margin-left: 0.2rem;
          transform: translateY(-3px);
        }
      }
      .input-box {
        margin-bottom: 0.7rem;
        input {
          width: 100%;
          border: 1px solid #d6dbe1;
          padding: 1.4rem 1.8rem 1.5rem;
          border-radius: 0.8rem;
        }
      }
      .text-box-wrap {
        flex: 1;
        .text-box {
          flex: 1;
          border-radius: 0.8rem;
          border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
          overflow: hidden;
          padding: 1.4rem 1.8rem;
          font-size: 1.4rem;
          font-weight: 500;
          height: 100%;
          textarea {
            width: 100%;
            height: 100%;
            flex: 1;
            border: none;
            &::placeholder {
              color: ${(props) => props.theme.searchPlaceholderColor};
              font-size: 1.4rem;
              font-weight: 00;
            }
          }
        }
      }
    }
  }
`;

const Btn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;

const ValTxt = styled.span`
  color: ${(props) => props.theme.errorColor};
  font-size: 1.2rem;
  font-weight: 400;
  display: flex;
  margin: 1rem 1.7rem 0;
`;
